import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            const to = loc.state?.from || "/app";
            nav(to, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const applyDemo = (demoEmail) => {
        setEmail(demoEmail);
        setPassword("Demo1234!");
    };

    return (
        <div className="min-h-screen wfos-hero-bg flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 mb-8">
                    <span className="atlas-logo-mark w-8 h-8 text-[13px]">N</span>
                    <span className="text-[15px] font-semibold tracking-tight text-gray-900">Nexus One</span>
                </Link>

                <div className="wfos-card p-7">
                    <h1 className="text-2xl font-medium tracking-tight text-gray-900">Sign in</h1>
                    <p className="mt-1 text-sm text-gray-500">Welcome back to your workforce.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div>
                            <label className="wfos-label">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <label className="wfos-label">Password</label>
                            <input
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit"
                            className="w-full h-10 rounded-lg bg-[#16a34a] text-white text-sm font-medium hover:bg-[#15803d] disabled:opacity-60"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-[#e8e8e3]">
                        <div className="wfos-label mb-2">Try a demo account</div>
                        <div className="grid grid-cols-1 gap-2">
                            {[
                                { label: "Acme Corp · Business",       email: "admin@acme.com" },
                                { label: "Meridian Health · Pro",      email: "admin@meridian.com" },
                                { label: "GovCloud Agency · Enterprise", email: "admin@govcloud.gov" },
                            ].map((d) => (
                                <button
                                    key={d.email}
                                    type="button"
                                    onClick={() => applyDemo(d.email)}
                                    data-testid={`demo-${d.email}`}
                                    className="text-left rounded-lg border border-[#e8e8e3] bg-[#fafaf7] px-3 py-2 text-xs hover:border-[#d6d6cf]"
                                >
                                    <div className="font-medium text-gray-900">{d.label}</div>
                                    <div className="text-gray-500">{d.email} · Demo1234!</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 text-sm text-gray-500 text-center">
                        New here? <Link to="/signup" className="text-[#16a34a] hover:text-[#15803d] font-medium">Start free</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
